import { Trans, useLingui } from '@lingui/react/macro';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useRouter } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { setActiveBrandId, useActiveBrandId } from '../lib/active-brand.js';
import { brandDecisionCountQuery } from '../lib/agency-home-api.js';
import { brandsQuery, meQuery } from '../lib/queries.js';
import { CreateBrandModal } from './create-brand-modal.js';
import { NotificationsBell } from './notifications-bell.js';
import { TokenBalanceChip } from './token-balance-chip.js';
import { UserMenu } from './user-menu.js';
import { WorkspaceBadge } from './workspace-badge.js';

// Workspace top nav — design handoff (Home) §5. 56px sticky cream bar with
// wordmark, tab row, MAG ACTIVE pill, and avatar. Shared across
// authenticated routes so the chrome stays consistent.

export type TopNavTab = 'home' | 'channels' | 'campaigns' | 'creative' | 'analytics' | 'settings';

interface TabDef {
  key: TopNavTab;
  label: string;
  to?:
    | '/home'
    | '/channels'
    | '/campaigns'
    | '/creative'
    | '/analytics'
    | '/brands'
    | '/settings/ad-accounts';
  /** Stub tabs read as in-product but inert until the matching feature
   *  lands. Per spec we ship the visual scaffolding now so the founder sees
   *  the product surface area immediately. */
  stub?: boolean;
}

// Brief was dropped from the top nav in s144 — it's a settings-shaped
// surface (founder configures their brand, doesn't run things from it
// day-to-day), so it lives under /brand/<slug>/settings now. The
// per-brand brief routes still render with active='settings' so the
// founder lands on a sensible parent.
const TABS: TabDef[] = [
  { key: 'home', label: 'Home', to: '/home' },
  // Channels — the perpetual surfaces mag operates on the founder's
  // behalf (instagram today; linkedin / tiktok / youtube once their
  // adapters ship). Routes to the per-brand /brand/<slug>/channels
  // index via the /channels redirector.
  { key: 'channels', label: 'Channels', to: '/channels' },
  // The 'Brand' tab was retired alongside the brand-hub UI in favour
  // of the brand workbench drawer (floating bubble + bottom-up panel,
  // visible on every brand-scoped page). See
  // the brand workbench design doc (CommitMind). The flat `/brand` URL still
  // redirects to the active brand's /home with the drawer pre-opened
  // for bookmarks + post-onboarding handoff.
  { key: 'campaigns', label: 'Campaigns', to: '/campaigns' },
  // Creative playground (M1 s159) — founder types a brief, mag drafts
  // + renders. DEV-ONLY (s265): in practice only mag drives the
  // creative pipeline, so the human-facing playground is hidden from
  // production and kept available in dev to watch creatives generate.
  // The `import.meta.env.DEV` guard tree-shakes the tab out of prod
  // builds; the routes themselves bounce to /home in prod (see
  // routes/creative.tsx + brand.$slug.creative.tsx). The chromeless
  // /render/creative/$id surface mag's pipeline uses is untouched.
  ...(import.meta.env.DEV
    ? [{ key: 'creative', label: 'Creative', to: '/creative' } satisfies TabDef]
    : []),
  // Analytics is the install-drawer + back-catalog of Mag's reads. Findings
  // still land in /home as feed cards; this tab is the front door for
  // setup + the "what mag's been noticing" archive view.
  { key: 'analytics', label: 'Analytics', to: '/analytics' },
  // Settings → ad-accounts today. Once we add more settings (notifications,
  // preferences, billing) this routes to a /settings index instead.
  // The Followups tab (s166) was retired in s264 — the cross-campaign
  // "what mag is chasing me on" view now lives as a rail teaser on
  // /home that opens a triage drawer (?triage=followups). /followups
  // and /brand/$slug/followups stay alive as redirectors for bookmarks.
  { key: 'settings', label: 'Settings', to: '/settings/ad-accounts' },
];

export interface TopNavProps {
  /** Which tab gets the yellow eye-dot. Pass `null` on sub-views (e.g.
   *  /activity) so no tab is highlighted. */
  active: TopNavTab | null;
}

export function TopNav({ active }: TopNavProps) {
  const { t } = useLingui();
  // Mobile menu disclosure (s152). Below md the tab row collapses
  // into a hamburger that opens a slide-down panel with the same
  // tabs. Click-outside closes; route change auto-closes via the
  // location effect below.
  const [mobileOpen, setMobileOpen] = useState(false);
  const _location = useLocation();
  useEffect(() => {
    setMobileOpen(false);
  }, []);

  // [designer-role] Designer-only users see a stripped-down chrome:
  // no brand switcher, no tab row, no token chip — surfaces they
  // can't reach. The wordmark links to /designer (their landing) and
  // a small "designer" badge sits where the workspace/brand
  // breadcrumb would be. Notifications + user menu still render so
  // they can still see system pings + log out.
  const { data: me } = useQuery(meQuery());
  const designerOnly = me?.designerOnly === true;

  if (designerOnly) return <DesignerTopNav />;

  return (
    // [2026-10-03] min-w-0 / shrink-0 are load-bearing, not tidying. This
    // is justify-between with two flex groups and nothing that could
    // shrink: at 390pt the left group (logo + workspace badge + brand
    // switcher) plus the right group (tokens + bell + avatar + burger)
    // overflowed, and since an overflowing flex row pushes past the
    // viewport rather than clipping inside it, the hamburger ended up
    // off-screen entirely — worse when the bell gained an unread count.
    // The left group may now shrink and truncate; the right group may
    // not, because every item in it is a control.
    <header className="sticky top-0 z-30 flex h-14 items-center justify-between gap-2 border-b border-[var(--line)] bg-[var(--paper)] px-4 md:px-7">
      <div className="flex min-w-0 items-center gap-3 md:gap-6">
        <Link to="/home" className="flex shrink-0 items-center gap-2 no-underline">
          <span className="relative font-display text-[18px] font-black tracking-[-0.04em] text-[var(--ink)]">
            magig
            <span className="absolute -top-0.5 left-[52%] text-[6px] leading-none text-[var(--spell)]">
              ✦
            </span>
          </span>
        </Link>
        {/* Context breadcrumb: workspace › brand › tabs. The tab row
            lives inside the same flex group as the pills so a ">"
            joins the active brand to the section the founder is in —
            the whole chrome reads as one continuous breadcrumb instead
            of "context pills, big gap, separate nav." Pointer-stability
            across gaps comes from the global `nav, [role='group']`
            rule in index.css; this wrapper opts in via role="group". */}
        {/* The workspace badge and its separator are the first thing to
            go below md: the brand switcher already names where you are,
            and the workspace is a once-a-session fact. TabsNav is
            hidden md:flex of its own accord (the burger replaces it). */}
        {/* biome-ignore lint/a11y/useSemanticElements: <fieldset> is form-control semantics, not what we want here — this is a navigation breadcrumb already containing a nested <nav>, so role="group" is the correct ARIA role for the assistive-tech grouping signal */}
        <div role="group" className="group/breadcrumb flex min-w-0 items-center gap-1.5">
          <span className="hidden items-center gap-1.5 md:inline-flex">
            <WorkspaceBadge />
            <BrandBreadcrumbSeparator />
          </span>
          <BrandSwitcher />
          <BrandBreadcrumbSeparator />
          <TabsNav active={active} />
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-2 md:gap-3">
        {/* MAG ACTIVE pill removed in s178.7g — it was decorative
            reassurance without any real state behind the pulse. The
            notifications bell now carries the "she's here" weight
            when there's actual signal (unread → pill with count). */}
        {/* [s264] Mag-tokens balance chip — always-visible counter so
            the founder knows where they stand. Hidden below sm to
            keep the bell + avatar from crowding on mobile. */}
        <TokenBalanceChip />
        <NotificationsBell />
        <UserMenu />
        {/* Mobile-only menu trigger. md:hidden keeps it off desktop. */}
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label={t`open menu`}
          aria-expanded={mobileOpen}
          className="md:hidden inline-flex h-9 w-9 items-center justify-center rounded-md border border-[var(--line)] bg-white text-[var(--ink)]"
        >
          {mobileOpen ? (
            <span className="text-[16px] leading-none">×</span>
          ) : (
            <span className="flex flex-col gap-[3px]">
              <span className="block h-[1.5px] w-[14px] bg-[var(--ink)]" />
              <span className="block h-[1.5px] w-[14px] bg-[var(--ink)]" />
              <span className="block h-[1.5px] w-[14px] bg-[var(--ink)]" />
            </span>
          )}
        </button>
      </div>
      {mobileOpen ? (
        <nav className="md:hidden absolute left-0 right-0 top-14 z-40 border-b border-[var(--line)] bg-[var(--cream)] shadow-[0_8px_18px_-12px_rgba(15,14,23,0.25)]">
          <ul className="flex flex-col py-2">
            {TABS.map((tab) => {
              const isActive = tab.key === active;
              const cls = `block px-5 py-3 text-[15px] no-underline ${
                isActive
                  ? 'font-bold text-[var(--ink)]'
                  : tab.stub
                    ? 'text-[var(--ink)]/45'
                    : 'text-[var(--ink)]/80 hover:text-[var(--ink)]'
              }`;
              if (tab.to && !tab.stub) {
                return (
                  <li key={tab.key}>
                    <Link to={tab.to} className={cls}>
                      {tab.label}
                    </Link>
                  </li>
                );
              }
              return (
                <li key={tab.key}>
                  <span className={`${cls} cursor-default`} aria-disabled={tab.stub}>
                    {tab.label}
                    {tab.stub ? (
                      <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/35">
                        <Trans>soon</Trans>
                      </span>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
        </nav>
      ) : null}
    </header>
  );
}

/** [designer-role] Stripped-down chrome for designer-only users.
 *  Same height + position as the main TopNav so designer pages
 *  align with the rest of the dashboard; middle of the bar is just
 *  a "designer" label instead of the workspace/brand/tab breadcrumb.
 *  Right side keeps the notifications bell + user menu so designers
 *  can see pings and log out. */
function DesignerTopNav() {
  return (
    <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-[var(--line)] bg-[var(--paper)] px-4 md:px-7">
      <div className="flex items-center gap-3 md:gap-4">
        <Link to="/designer" className="flex items-center gap-2 no-underline">
          <span className="relative font-display text-[18px] font-black tracking-[-0.04em] text-[var(--ink)]">
            magig
            <span className="absolute -top-0.5 left-[52%] text-[6px] leading-none text-[var(--spell)]">
              ✦
            </span>
          </span>
        </Link>
        <span aria-hidden="true" className="select-none text-[14px] text-[var(--ink)]/30">
          ›
        </span>
        <span className="inline-flex items-center rounded-full border border-[var(--sage)]/40 bg-[var(--sage)]/15 px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--sage)]">
          <Trans>designer</Trans>
        </span>
      </div>
      <div className="flex items-center gap-3">
        <NotificationsBell />
        <UserMenu />
      </div>
    </header>
  );
}

/** "›" between the workspace badge and the brand switcher. Only
 *  renders when the org has at least one brand — otherwise we'd show a
 *  trailing chevron pointing at nothing. Mirrors BrandSwitcher's own
 *  "no brands → render nothing" rule so the breadcrumb stays coherent.
 *  Opacity tracks the parent group's hover so the breadcrumb reads as
 *  one continuous element when any chip is hovered. */
function BrandBreadcrumbSeparator() {
  const { data: brands } = useQuery(brandsQuery());
  if (!brands || brands.length === 0) return null;
  return (
    <span
      aria-hidden="true"
      className="select-none text-[14px] text-[var(--ink)]/30 transition-colors duration-150 ease-out group-hover/breadcrumb:text-[var(--ink)]/55"
    >
      ›
    </span>
  );
}

/** Brand switcher next to the wordmark. Always visible when the org
 *  has at least one brand — the chip is also the founder's anchor to
 *  "what brand am I in" even when there's only one. Hidden only when
 *  the org has zero brands (just-signed-up or just-deleted-them-all);
 *  the rest of the app redirects to /brands in that case. Click → menu
 *  with siblings, "add new brand", and "manage brands". Picking one
 *  writes to the active-brand store and invalidates every query cache
 *  so the next render fetches with the new ?brand= scope. */
function BrandSwitcher() {
  const { t } = useLingui();
  const { data: brands } = useQuery(brandsQuery());
  const activeBrandId = useActiveBrandId();
  const queryClient = useQueryClient();
  const router = useRouter();
  const location = useLocation();
  // Rollup view (/home?view=all) was paused during the URL restructure
  // because /home now redirects to /brand/$active/home. Variable kept
  // around (always false today) for the chip label + dropdown markers
  // — will light up again when the rollup gets its own route.
  const isRollupView = false;
  void location;
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Click-outside to close. Listening on mousedown (not click) so the
  // close fires before any button inside the menu runs its own handler.
  useEffect(() => {
    if (!open) return undefined;
    const onMouseDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  if (!brands || brands.length === 0) return null;

  const active = brands.find((b) => b.id === activeBrandId) ?? brands[0];
  if (!active) return null;

  const handlePick = async (target: { id: string; slug: string }) => {
    setActiveBrandId(target.id);
    setOpen(false);
    // Bust the per-brand caches so the next render fetches fresh. Then
    // navigate URL-first to /brand/<slug>/home — the URL becomes the
    // source of truth for which brand the founder is in, with
    // localStorage as a fallback for default-routing only.
    //
    // [task bf4fa14] refetchType 'none' — mark everything stale, refetch
    // nothing. This used to be a bare invalidateQueries(), which defaults to
    // refetchType 'active' and so re-ran EVERY live query on the page we are
    // about to leave: feed, campaigns, brain, analytics, rail, the lot. The
    // founder waited on a round of requests whose results were thrown away a
    // moment later when the route unmounted.
    //
    // Blanket staleness is still right, and narrowing by key would be wrong:
    // most queries are keyed by brandId and need no busting at all, but a few
    // (importedCampaignsQuery, keyed ['imported-campaigns']) hit endpoints that
    // resolve the brand server-side, so their cache entry is shared across
    // brands and WOULD go stale. Marking all of them costs nothing now that
    // nothing refetches eagerly.
    // Awaited, though it does no network work — refetchType 'none' resolves
    // immediately. Awaiting beats a floating promise or a void: there is no
    // cost to pay and no rejection to drop on the floor.
    await queryClient.invalidateQueries({ refetchType: 'none' });
    // ...with one exception that must actually come back before we navigate.
    // beforeLoad reads the brand list to resolve the slug, and the /home gate
    // reads brand.onboardedAt off it — and ensureQueryData serves STALE data
    // rather than revalidating, so a merely-invalidated list would hand the
    // route the old onboarding state.
    await queryClient.invalidateQueries({ queryKey: ['brands'] });
    await router.navigate({ to: '/brand/$slug/home', params: { slug: target.slug } });
  };

  return (
    <>
      <div className="relative" ref={menuRef}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          // Microanimations match WorkspaceBadge: subtle hover lift +
          // border darkens, chevron rotates 180° on open, favicon
          // scales up slightly on hover. Easing matches the
          // image-preview-modal's ease-out-quart curve.
          className={`group flex items-center gap-1.5 rounded-full border bg-white py-1 pl-1 pr-2.5 font-mono text-[10.5px] font-semibold uppercase tracking-[0.08em] text-[var(--ink)]/75 transition-all duration-150 ease-out hover:-translate-y-px hover:border-[var(--ink)]/40 hover:text-[var(--ink)] ${
            open ? 'border-[var(--ink)]/60' : 'border-[var(--line)]'
          }`}
          title={t`switch brand`}
        >
          {isRollupView ? (
            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-[var(--line)] bg-[var(--paper)] text-[9px] font-bold transition-transform duration-150 ease-out group-hover:scale-105">
              ∗
            </span>
          ) : active.faviconUrl || active.logoUrl ? (
            // Phase 4.28a — prefer the scraped favicon for the chip; the
            // favicon is already shaped for a small circle, the full logo
            // often isn't.
            <img
              src={active.faviconUrl ?? active.logoUrl ?? ''}
              alt=""
              className="h-5 w-5 rounded-full border border-[var(--line)] object-cover transition-transform duration-150 ease-out group-hover:scale-105"
            />
          ) : (
            <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-[var(--plum)] transition-transform duration-150 ease-out group-hover:scale-125" />
          )}
          {/* Brand name hides on mobile — the logo + dropdown chevron are
            enough to convey "this is the active brand, tap to switch".
            Full name renders on sm+ where there's room. */}
          <span className="hidden sm:inline">
            {isRollupView ? <Trans>all brands</Trans> : active.name}
          </span>
          <span
            className={`text-[var(--ink)]/40 transition-transform duration-150 ease-out group-hover:translate-y-0.5 ${
              open ? 'rotate-180 text-[var(--ink)]/70' : ''
            }`}
          >
            ↓
          </span>
        </button>
        <AnimatePresence>
          {open ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: -4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: -4 }}
              transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
              style={{ transformOrigin: 'top left' }}
              className="absolute left-0 top-full z-40 mt-2 w-64 overflow-hidden rounded-lg border border-[var(--ink)] bg-white shadow-[4px_4px_0_var(--plum)]"
            >
              <p className="border-b border-[var(--line)] px-3 py-2 font-mono text-[9.5px] font-semibold uppercase tracking-[0.12em] text-[var(--ink)]/55">
                {'↘'} <Trans>brands</Trans>
              </p>
              <ul className="flex flex-col">
                {brands.map((b) => {
                  const isActive = b.id === active.id && !isRollupView;
                  return (
                    <li key={b.id}>
                      <button
                        type="button"
                        onClick={() => handlePick({ id: b.id, slug: b.slug })}
                        className={`flex w-full items-center gap-2 px-3 py-2 text-left text-[12.5px] hover:bg-[var(--paper)] ${
                          isActive ? 'font-bold text-[var(--ink)]' : 'text-[var(--ink)]/80'
                        }`}
                      >
                        {b.faviconUrl || b.logoUrl ? (
                          <img
                            src={b.faviconUrl ?? b.logoUrl ?? ''}
                            alt=""
                            className="h-5 w-5 rounded-full border border-[var(--line)] object-cover"
                          />
                        ) : (
                          <span
                            className={`inline-block h-1.5 w-1.5 rounded-full ${
                              isActive ? 'bg-[var(--spell)]' : 'bg-[var(--ink)]/15'
                            }`}
                          />
                        )}
                        <span className="flex-1 truncate">{b.name}</span>
                        <BrandDecisionCountBadge brandId={b.id} />
                      </button>
                    </li>
                  );
                })}
              </ul>
              <div className="flex flex-col border-t border-[var(--line)]">
                {/* "manage brands" moved out of this dropdown — the workspace
                badge's ↘ brands entry is the single home for that. Only
                the create affordance stays here since it's a fast-path
                action ("switch to a new brand right now"). */}
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    setCreateOpen(true);
                  }}
                  className="block w-full px-3 py-2 text-left text-[12px] font-semibold text-[var(--plum)] hover:bg-[var(--paper)]"
                >
                  {'↘'} <Trans>add new brand</Trans>
                </button>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
      <CreateBrandModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  );
}

/** Per-brand badge in the switcher dropdown. P1 stub of the agency-
 *  home decisions queue (the agency home design doc (CommitMind)): when a brand
 *  has pending decisions waiting on the founder's call, surface that
 *  count here so a quiet brand can't hide one. P1 always reads 0 —
 *  the slot is wired but the hook returns the stub value. P3 swaps
 *  the hook to read from the classifier output (cards where
 *  classification = 'decision' for this brand). */
function BrandDecisionCountBadge({ brandId }: { brandId: string }) {
  const count = useBrandDecisionCount(brandId);
  if (count === 0) return null;
  return (
    <span
      role="status"
      aria-label={`${count} pending decision${count === 1 ? '' : 's'}`}
      className="inline-flex shrink-0 items-center justify-center rounded-full bg-[var(--spell)] px-1.5 py-0.5 font-mono text-[9.5px] font-bold leading-none text-[var(--ink)]"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

/** P3a — reads the real per-brand decision count from the classifier
 *  endpoint (GET /api/brands/:id/decisions/count). One query per brand
 *  in the switcher dropdown; cached 30s so opening/closing the dropdown
 *  doesn't refetch storm. Returns 0 while the first fetch is in flight
 *  so the badge simply isn't shown rather than flashing a stale value. */
function useBrandDecisionCount(brandId: string): number {
  const { data } = useQuery(brandDecisionCountQuery(brandId));
  return data?.count ?? 0;
}

/** Desktop tab nav with a single measured indicator that slides between
 *  tab positions on route change. Per-tab refs feed a useLayoutEffect
 *  that measures the active tab's bounding rect and positions one
 *  absolutely-positioned dot accordingly. The dot's `left + width`
 *  transition then carries the animation — same yellow circle as
 *  before, just one of them instead of N (where only the active was
 *  ever rendered). [12.2L-40] Tier 1. */
function TabsNav({ active }: { active: TopNavTab | null }) {
  // [spec 3a365634] "is there a campaign wanting me?" — read off the
  // badge endpoint the switcher already polls, so this costs no new
  // request. Zero while unresolved, so the mark appears rather than
  // flashing a stale one.
  const tabsBrandId = useActiveBrandId();
  const { data: brandAsks } = useQuery({
    ...brandDecisionCountQuery(tabsBrandId ?? ''),
    enabled: Boolean(tabsBrandId),
  });
  const campaignsNeedingYou = brandAsks?.campaignsNeedingYou ?? 0;
  const { t } = useLingui();
  const containerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Map<TopNavTab, HTMLElement | null>>(new Map());
  const [indicator, setIndicator] = useState<{ left: number; width: number } | null>(null);

  useLayoutEffect(() => {
    if (!active) {
      setIndicator(null);
      return;
    }
    const tabEl = tabRefs.current.get(active);
    const containerEl = containerRef.current;
    if (!tabEl || !containerEl) {
      setIndicator(null);
      return;
    }
    const tabRect = tabEl.getBoundingClientRect();
    const containerRect = containerEl.getBoundingClientRect();
    setIndicator({ left: tabRect.left - containerRect.left, width: tabRect.width });
  }, [active]);

  return (
    <nav ref={containerRef} className="relative hidden md:flex">
      {TABS.map((tab) => {
        const isActive = tab.key === active;
        const inner = (
          <span
            ref={(el) => {
              tabRefs.current.set(tab.key, el);
            }}
            className={`relative inline-block px-3.5 py-2 text-[13px] ${
              isActive ? 'font-bold opacity-100' : 'font-medium opacity-55'
            } ${tab.stub ? 'cursor-default' : 'cursor-pointer hover:opacity-100'}`}
          >
            {tab.label}
            {/* DELIBERATELY NOT THE SAME MARK as the active-tab dot
                below, which is yellow, 6px and sits under the tab. Two
                signals wearing one mark is how both stop meaning
                anything — and the founder's screenshot had the yellow
                dot on Campaigns while that brand had two unanswered
                cards, which is exactly the confusion. Plum, smaller,
                and up at the label's shoulder. */}
            {tab.key === 'campaigns' && campaignsNeedingYou > 0 ? (
              <span
                aria-hidden="true"
                className="absolute right-0.5 top-1 h-[5px] w-[5px] rounded-full bg-[var(--plum)]"
              />
            ) : null}
          </span>
        );
        if (tab.to && !tab.stub) {
          return (
            <Link
              key={tab.key}
              to={tab.to}
              className="no-underline text-[var(--ink)]"
              {...(tab.key === 'campaigns' && campaignsNeedingYou > 0
                ? // The dot is aria-hidden, so the fact it carries has to
                  // reach a screen reader some other way.
                  { 'aria-description': t`${campaignsNeedingYou} campaigns need you` }
                : {})}
            >
              {inner}
            </Link>
          );
        }
        return (
          <span
            key={tab.key}
            aria-disabled={tab.stub ? true : undefined}
            title={tab.stub ? 'coming soon' : undefined}
          >
            {inner}
          </span>
        );
      })}
      {indicator ? (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute h-[6px] w-[6px] rounded-full border-[1.2px] border-[var(--ink)] bg-[var(--spell)]"
          style={{
            left: indicator.left + indicator.width / 2 - 3,
            bottom: -16,
            transition: 'left 220ms cubic-bezier(0.2, 0.7, 0.3, 1)',
          }}
        />
      ) : null}
    </nav>
  );
}
